import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import Spinners from "components/Common/Spinner";
import { getAllPromoters } from "api";
import moment from "moment";

// Read-only admin listing of every organizer's promoters.
// "User" is the users_tbl row with the same email, if that person already registered.

const formatMoney = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

const formatUser = (user) => {
  if (!user) return "Not registered";
  return user.name || user.username || user.email || `User #${user.userId}`;
};

function AdminPromoters() {
  document.title = "Promoter List | EventoX";

  const [promoters, setPromoters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modal, setModal] = useState(false);

  const loadPromoters = async () => {
    setIsLoading(true);
    try {
      const data = await getAllPromoters();
      setPromoters(Array.isArray(data) ? data : []);
    } catch (error) {
      setPromoters([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPromoters();
  }, []);

  const handleView = (row) => {
    setSelectedItem(row);
    setModal(true);
  };

  const columns = useMemo(
    () => [
      {
        header: "Promoter",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <div>
              <div className="fw-medium">{row.name || "—"}</div>
              <div className="text-muted small">{row.email || ""}</div>
              {row.phone ? <div className="text-muted small">{row.phone}</div> : null}
            </div>
          );
        },
      },
      {
        header: "Organizer",
        accessorKey: "Organizer.name",
        enableColumnFilter: false,
        cell: (cell) => {
          const organizer = cell.row.original.Organizer;
          if (!organizer) return "—";
          return (
            <div>
              <div>{organizer.name || "—"}</div>
              {organizer.email ? <div className="text-muted small">{organizer.email}</div> : null}
            </div>
          );
        },
      },
      {
        header: "User",
        accessorKey: "User.name",
        enableColumnFilter: false,
        cell: (cell) => {
          const user = cell.row.original.User;
          if (!user) return <span className="text-muted">Not registered</span>;
          return (
            <div>
              <div>{formatUser(user)}</div>
              <div className="text-muted small">ID {user.userId}</div>
            </div>
          );
        },
      },
      {
        header: "Coupon",
        accessorKey: "couponCode",
        enableColumnFilter: false,
      },
      {
        header: "Discount %",
        accessorKey: "couponDiscountPercentage",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue() != null ? `${cell.getValue()}%` : "—"}</span>,
      },
      {
        header: "Commission",
        accessorKey: "commissionPerTicket",
        enableColumnFilter: false,
        cell: (cell) => formatMoney(cell.getValue()),
      },
      {
        header: "Total Tickets",
        accessorKey: "totalTickets",
        enableColumnFilter: false,
      },
      {
        header: "Total Earnings",
        accessorKey: "totalEarned",
        enableColumnFilter: false,
        cell: (cell) => formatMoney(cell.getValue()),
      },
      {
        header: "Country",
        accessorKey: "Country.countryName",
        enableColumnFilter: false,
        cell: (cell) => cell.row.original.Country?.countryName || "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell) => (
          <span
            className={`badge font-size-11 m-1 ${
              cell.getValue() === "active" ? "badge-soft-primary" : "badge-soft-danger"
            }`}
          >
            {cell.getValue() || "—"}
          </span>
        ),
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => (
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => handleView(cell.row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const detailRows = selectedItem
    ? [
        { label: "Promoter", value: selectedItem.name },
        { label: "Email", value: selectedItem.email },
        { label: "Phone", value: selectedItem.phone },
        { label: "Status", value: selectedItem.status },
        { label: "Organizer", value: selectedItem.Organizer?.name },
        { label: "Organizer Email", value: selectedItem.Organizer?.email },
        { label: "User", value: formatUser(selectedItem.User) },
        { label: "User ID", value: selectedItem.User?.userId },
        { label: "User Promoter Flag", value: selectedItem.User ? String(!!selectedItem.User.isPromoter) : "—" },
        { label: "Country", value: selectedItem.Country?.countryName },
        { label: "Coupon Code", value: selectedItem.couponCode },
        { label: "Discount %", value: selectedItem.couponDiscountPercentage != null ? `${selectedItem.couponDiscountPercentage}%` : "—" },
        { label: "Coupon Used", value: selectedItem.Coupon?.totalUsed },
        { label: "Commission / Ticket", value: formatMoney(selectedItem.commissionPerTicket) },
        { label: "Total Tickets", value: selectedItem.totalTickets || 0 },
        { label: "Total Discount", value: formatMoney(selectedItem.totalDiscount) },
        { label: "Total Earnings", value: formatMoney(selectedItem.totalEarned) },
        { label: "Bookings", value: selectedItem.bookingCount || 0 },
        {
          label: "Created At",
          value: selectedItem.createdAt
            ? moment(selectedItem.createdAt).format("DD-MM-YYYY HH:mm:ss")
            : "—",
        },
      ]
    : [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Promoter List" />
          <Row>
            {isLoading ? (
              <Spinners setLoading={setIsLoading} />
            ) : (
              <Col lg="12">
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={promoters || []}
                      isGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search promoters..."
                      isCustomPageSize={true}
                      tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination"
                    />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)} tag="h4">
          Promoter Details
        </ModalHeader>
        <ModalBody>
          <table className="table table-sm table-bordered mb-0">
            <tbody>
              {detailRows.map((row) => (
                <tr key={row.label}>
                  <th style={{ width: "40%" }}>{row.label}</th>
                  <td>{row.value == null || row.value === "" ? "—" : String(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default withRouter(AdminPromoters);
